import { useEffect, useState, useCallback } from "react";
import { api, DOMAINS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoCard from "@/components/PhotoCard";
import QuickEditPanel from "@/components/QuickEditPanel";
import MergeDialog from "@/components/MergeDialog";
import AddToAlbumDialog from "@/components/AddToAlbumDialog";
import { Images, FunnelSimple, Copy, CheckSquare, X, CheckCircle, Trash, Globe, Star, Sparkle, FolderSimplePlus } from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const STATUSES = ["analyzing", "analyzed", "needs_review", "approved", "published"];

export default function Library() {
  const { user } = useAuth();
  const isAdmin = !!(user && (user.is_admin || user.role === "admin"));
  const [photos, setPhotos] = useState(null);
  const [status, setStatus] = useState("all");
  const [domain, setDomain] = useState("all");
  const [loc, setLoc] = useState("all"); // all | gps | ai | none
  const [dupOnly, setDupOnly] = useState(false);
  const [aiTitledOnly, setAiTitledOnly] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [editPhoto, setEditPhoto] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [mergePhoto, setMergePhoto] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [publishAllBusy, setPublishAllBusy] = useState(false);
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (domain !== "all") params.domain = domain;
    if (loc === "gps") params.location_source = "gps";
    if (loc === "ai") params.location_source = "ai";
    if (loc === "none") params.location_source = "none";
    if (dupOnly) params.duplicates = true;
    setPhotos(null);
    api.get("/photos", { params }).then((r) => setPhotos(r.data)).catch(() => setPhotos([]));
  }, [status, domain, loc, dupOnly]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    await api.post(`/photos/${id}/status`, { status: "approved" });
    setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, status: "approved" } : p)));
    toast.success("Approved");
  };
  const remove = async (id, isDup) => {
    await api.delete(`/photos/${id}`);
    setPhotos((ps) => ps.filter((p) => p.id !== id));
    toast.success(isDup ? "Duplicate deleted" : "Photo deleted");
  };
  const changeDomain = async (id, primary_domain) => {
    await api.post(`/photos/${id}/edit`, { primary_domain });
    setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, primary_domain } : p)));
    toast.success(`Category → ${primary_domain}`);
  };
  const openEdit = (photo) => { setEditPhoto(photo); setEditOpen(true); };
  const onEdited = (id, updated) => {
    setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, ...updated } : p)));
  };
  const openMerge = (photo) => { setMergePhoto(photo); setMergeOpen(true); };
  const onMerged = (dupId, origId, original) => {
    setPhotos((ps) => ps
      .filter((p) => p.id !== dupId)
      .map((p) => (p.id === origId ? { ...p, ...original } : p)));
  };
  const toggleFeature = async (id, featured) => {
    try {
      await api.post(`/photos/${id}/feature`, { featured });
      setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, editors_pick: featured } : p)));
      toast.success(featured ? "Added to Editor's Picks ⭐" : "Removed from Editor's Picks");
    } catch { toast.error("Could not update Editor's Picks"); }
  };
  const removeStarter = async () => {
    if (!window.confirm("Hide the 31 starter (stock) photos and stop them coming back on future deploys? Your own photos stay untouched.")) return;
    try {
      const r = await api.post("/admin/remove-starter-collection");
      toast.success(`Removed ${r.data.removed} starter photo${r.data.removed === 1 ? "" : "s"}. Your gallery is now all yours.`);
      load();
    } catch { toast.error("Could not remove starter collection"); }
  };

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const clearSelection = () => { setSelected(new Set()); };
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };
  const selectAll = () => setSelected(new Set((photos || []).map((p) => p.id)));

  const bulkApprove = async () => {
    const ids = [...selected];
    setBulkBusy(true);
    try {
      const r = await api.post("/photos/bulk", { ids, action: "approve" });
      setPhotos((ps) => ps.map((p) => (selected.has(p.id) ? { ...p, status: "approved" } : p)));
      toast.success(`Approved ${r.data.count} photo${r.data.count === 1 ? "" : "s"}`);
      exitSelectMode();
    } catch { toast.error("Bulk approve failed"); }
    finally { setBulkBusy(false); }
  };
  const bulkDelete = async () => {
    const ids = [...selected];
    setBulkBusy(true);
    try {
      const r = await api.post("/photos/bulk", { ids, action: "delete" });
      setPhotos((ps) => ps.filter((p) => !selected.has(p.id)));
      toast.success(`Deleted ${r.data.count} photo${r.data.count === 1 ? "" : "s"}`);
      exitSelectMode();
    } catch { toast.error("Bulk delete failed"); }
    finally { setBulkBusy(false); }
  };
  const bulkPublish = async () => {
    const ids = [...selected];
    setBulkBusy(true);
    try {
      const r = await api.post("/photos/bulk", { ids, action: "publish" });
      const skippedSet = new Set(r.data.skipped_ids || []);
      setPhotos((ps) => ps.map((p) => (selected.has(p.id) && !skippedSet.has(p.id)
        ? { ...p, status: "published", privacy: "public", reuse_permission: "public_attribution" }
        : p)));
      if (r.data.count) {
        toast.success(`Published ${r.data.count} photo${r.data.count === 1 ? "" : "s"} to the public gallery`);
      }
      if (r.data.skipped) {
        toast.warning(`${r.data.skipped} photo${r.data.skipped === 1 ? "" : "s"} skipped — add a Title first (use Edit), then publish.`);
      }
      exitSelectMode();
    } catch { toast.error("Bulk publish failed"); }
    finally { setBulkBusy(false); }
  };

  const publishAllMine = async () => {
    setPublishAllBusy(true);
    try {
      const r = await api.post("/photos/publish-all-mine");
      if (r.data.count) toast.success(`Published ${r.data.count} photo${r.data.count === 1 ? "" : "s"} to the public gallery`);
      else toast.info("Nothing new to publish — all titled photos are already public.");
      if (r.data.skipped) toast.warning(`${r.data.skipped} photo${r.data.skipped === 1 ? "" : "s"} skipped — they need a Title first (use Edit).`);
      load();
    } catch { toast.error("Publish all failed"); }
    finally { setPublishAllBusy(false); }
  };

  const LOC_TABS = [
    { key: "all", label: "All" },
    { key: "gps", label: "GPS-tagged" },
    { key: "ai", label: "AI-located" },
    { key: "none", label: "No location" },
  ];

  const isAiTitled = (p) => (p.provenance?.title === "AI") && p.status !== "published";
  const aiTitledCount = (photos || []).filter(isAiTitled).length;
  const visiblePhotos = aiTitledOnly ? (photos || []).filter(isAiTitled) : photos;

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#F27A54]">Photo Library</p>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight">
            All Photos {photos && <span className="text-lg font-semibold text-muted-foreground">({photos.length})</span>}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <FunnelSimple size={18} className="text-muted-foreground" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="h-9 w-40 rounded-md"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={domain} onValueChange={setDomain}>
            <SelectTrigger data-testid="filter-domain" className="h-9 w-40 rounded-md"><SelectValue placeholder="Domain" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Domains</SelectItem>
              {DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Location-intelligence segmented filter */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border border-border bg-white p-1">
          {LOC_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`locfilter-${t.key}`}
              onClick={() => setLoc(t.key)}
              className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                loc === t.key ? "bg-[#F27A54] text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          data-testid="filter-duplicates"
          onClick={() => setDupOnly((v) => !v)}
          className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
            dupOnly ? "border-red-300 bg-red-50 text-red-600" : "border-border bg-white text-zinc-600 hover:bg-zinc-100"
          }`}
        >
          <Copy size={14} weight="bold" /> {dupOnly ? "Showing duplicates" : "Duplicates only"}
        </button>
        <button
          data-testid="toggle-select-mode"
          onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
          className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
            selectMode ? "border-[#F27A54] bg-blue-50 text-[#F27A54]" : "border-border bg-white text-zinc-600 hover:bg-zinc-100"
          }`}
        >
          <CheckSquare size={14} weight="bold" /> {selectMode ? "Exit selection" : "Select"}
        </button>
        <button
          data-testid="publish-all-mine"
          onClick={publishAllMine}
          disabled={publishAllBusy}
          className="flex items-center gap-1.5 rounded-md bg-[#F27A54] px-3 py-1.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-[#00238a] disabled:opacity-50"
        >
          <Globe size={14} weight="bold" /> {publishAllBusy ? "Publishing…" : "Publish all my photos"}
        </button>
        {aiTitledCount > 0 && (
          <button
            data-testid="filter-ai-titled"
            onClick={() => setAiTitledOnly((v) => !v)}
            className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
              aiTitledOnly ? "border-amber-400 bg-amber-50 text-amber-700" : "border-border bg-white text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            <Sparkle size={14} weight="bold" /> AI-titled ({aiTitledCount})
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="remove-starter-collection"
            onClick={removeStarter}
            className="flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-semibold text-zinc-500 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
          >
            <Trash size={14} weight="bold" /> Remove starter photos
          </button>
        )}
        <span className="text-xs text-muted-foreground">
          {selectMode ? "Tap photos to select, then publish, approve, or delete in bulk." : "Hover a photo for quick Approve · Edit · Merge · Delete."}
        </span>
      </div>

      {/* Bulk action bar */}
      {selectMode && (
        <div data-testid="bulk-action-bar" className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-[#F27A54]/30 bg-blue-50 px-4 py-2.5">
          <span data-testid="bulk-selected-count" className="text-sm font-bold text-[#F27A54]">
            {selected.size} selected
          </span>
          <button data-testid="bulk-select-all" onClick={selectAll} className="text-xs font-semibold text-zinc-600 underline-offset-2 hover:underline">
            Select all ({photos?.length || 0})
          </button>
          {selected.size > 0 && (
            <button data-testid="bulk-clear" onClick={clearSelection} className="text-xs font-semibold text-zinc-600 underline-offset-2 hover:underline">
              Clear
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="bulk-add-to-album" onClick={() => setAlbumDialogOpen(true)} disabled={selected.size === 0 || bulkBusy}
              className="flex items-center gap-1.5 rounded-md bg-[#2C2724] px-3 py-1.5 text-xs font-semibold text-white hover:bg-black disabled:opacity-50">
              <FolderSimplePlus size={14} weight="bold" /> Add to album
            </button>
            <button
              data-testid="bulk-publish" onClick={bulkPublish} disabled={selected.size === 0 || bulkBusy}
              className="flex items-center gap-1.5 rounded-md bg-[#F27A54] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#00238a] disabled:opacity-50">
              <Globe size={14} weight="bold" /> Publish to public gallery
            </button>
            <button
              data-testid="bulk-approve" onClick={bulkApprove} disabled={selected.size === 0 || bulkBusy}
              className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              <CheckCircle size={14} weight="bold" /> Approve selected
            </button>
            <button
              data-testid="bulk-delete" onClick={bulkDelete} disabled={selected.size === 0 || bulkBusy}
              className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              <Trash size={14} weight="bold" /> Delete selected
            </button>
            <button data-testid="bulk-exit" onClick={exitSelectMode} className="grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-white">
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* AI-title review nudge */}
      {aiTitledCount > 0 && !nudgeDismissed && !aiTitledOnly && (
        <div data-testid="ai-title-nudge" className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
          <Sparkle size={18} weight="fill" className="text-amber-500 shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-bold">{aiTitledCount} photo{aiTitledCount === 1 ? "" : "s"}</span> {aiTitledCount === 1 ? "has" : "have"} an AI-suggested title — review &amp; tweak the wording before publishing.
          </p>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="nudge-review" onClick={() => setAiTitledOnly(true)}
              className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600">
              Review them
            </button>
            <button data-testid="nudge-dismiss" onClick={() => setNudgeDismissed(true)}
              className="grid h-7 w-7 place-items-center rounded-md text-amber-700 hover:bg-amber-100">
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}
      {aiTitledOnly && (
        <div className="mt-4 flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5">
          <span className="text-sm font-semibold text-amber-800">Showing AI-titled photos awaiting review</span>
          <button onClick={() => setAiTitledOnly(false)} className="ml-auto text-xs font-semibold text-amber-700 underline-offset-2 hover:underline">Show all</button>
        </div>
      )}

      {photos === null ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-md" />)}
        </div>
      ) : visiblePhotos.length === 0 ? (
        <div className="mt-16 grid place-items-center text-center">
          <Images size={48} className="text-zinc-300" weight="thin" />
          <p className="mt-4 font-display text-lg font-bold">No photos found</p>
          <p className="text-sm text-muted-foreground">Adjust filters or import photos to get started.</p>
        </div>
      ) : (
        <div data-testid="library-grid" className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {visiblePhotos.map((p, i) => (
            <PhotoCard
              key={p.id} photo={p} index={i}
              onApprove={approve} onDelete={remove} onDomain={changeDomain} onEdit={openEdit} onMerge={openMerge}
              onFeature={isAdmin ? toggleFeature : undefined}
              selectable={selectMode} selected={selected.has(p.id)} onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      <QuickEditPanel
        photo={editPhoto} open={editOpen} onOpenChange={setEditOpen} onSaved={onEdited}
      />
      <MergeDialog
        photo={mergePhoto} open={mergeOpen} onOpenChange={setMergeOpen} onMerged={onMerged}
      />
      <AddToAlbumDialog
        open={albumDialogOpen} onOpenChange={setAlbumDialogOpen}
        photoIds={[...selected]}
        onDone={() => { exitSelectMode(); }}
      />
    </div>
  );
}
